import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, X, KeyRound, CreditCard, FolderLock, Wallet, StickyNote,
  Copy, Check, ExternalLink, ArrowRight, ShieldCheck, Eye, EyeOff,
  Sparkles, Download, Clock, TrendingUp, ChevronRight, FileText,
  FileSpreadsheet, FileArchive, CheckCircle2, Star, Tag, Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import api from '../../api/client';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../../crypto/WebCryptoProvider';

const TRENDING_TAGS = ['all', 'banking', 'cloud', 'security', 'personal', 'work'];


export default function MobileSearchExploreModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { vaultKey, user } = useAuth();
  const { hasPermission } = useApplications();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'passwords' | 'cards' | 'documents' | 'expenses'
  const [selectedTag, setSelectedTag] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [revealedIds, setRevealedIds] = useState({});

  // Real data state
  const [realCards, setRealCards] = useState([]);
  const [realDocs, setRealDocs] = useState([]);
  const [realPasswords, setRealPasswords] = useState([]);
  const [realExpenses, setRealExpenses] = useState([]);
  const [loadingReal, setLoadingReal] = useState(false);

  // Clipboard copy handler with haptic feedback
  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    if (navigator.vibrate) navigator.vibrate(30);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleReveal = (id) => {
    setRevealedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Load items from API
  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    async function loadData() {
      setLoadingReal(true);
      try {
        const vaultsRes = await api.get('/vaults').catch(() => ({ data: [] }));
        const primaryVault = vaultsRes.data?.[0];
        if (primaryVault && vaultKey) {
          const crypto = new VaultCrypto(WebCryptoProvider);
          const itemsRes = await api.get(`/vaults/${primaryVault.id}/items`).catch(() => ({ data: [] }));
          const items = itemsRes.data || [];

          const cards = [];
          const docs = [];
          const pwds = [];

          for (const item of items) {
            try {
              const raw = await crypto.decryptItem(item.encrypted_data, vaultKey, item.id);
              const p = JSON.parse(raw);
              if (['CARD', 'card', 'PAYMENT_CARD', 'CREDIT_CARD'].includes(item.item_type || p.itemType)) {
                cards.push({ ...p, id: item.id, type: 'card' });
              } else if (['DOCUMENT', 'document'].includes(item.item_type || p.itemType)) {
                docs.push({ ...p, id: item.id, type: 'document' });
              } else {
                pwds.push({ ...p, id: item.id, type: 'password' });
              }
            } catch (e) {}
          }

          if (active) {
            if (cards.length) setRealCards(cards);
            if (docs.length) setRealDocs(docs);
            if (pwds.length) setRealPasswords(pwds);
          }
        }

        // Expenses
        const expRes = await api.get('/expenses/dashboard').catch(() => ({ data: null }));
        if (expRes.data?.recentTransactions && active) {
          setRealExpenses(expRes.data.recentTransactions.map(t => ({
            id: t.id,
            type: 'expense',
            merchant: t.description || t.merchant || 'Expense',
            category: t.category || 'General',
            amount: t.amount,
            currency: '₹',
            date: t.date ? new Date(t.date).toLocaleDateString() : 'Recent',
            paymentMethod: t.payment_mode || 'Cash/Card',
            tags: [t.category?.toLowerCase() || 'expense']
          })));
        }
      } catch (err) {
        // Fallbacks will take over smoothly
      } finally {
        if (active) setLoadingReal(false);
      }
    }

    loadData();
    return () => { active = false; };
  }, [isOpen, vaultKey]);

  // Use real decrypted items only (no fake sample duplicates)
  const cardsList = realCards;
  const passwordsList = realPasswords;
  const docsList = realDocs;
  const expensesList = realExpenses;

  // Filter items by search query and selected tag
  const q = searchQuery.toLowerCase().trim();

  const filteredCards = useMemo(() => {
    return cardsList.filter(c => {
      const matchQuery = !q || (
        (c.cardName || c.title || '').toLowerCase().includes(q) ||
        (c.issuer || c.fields?.issuer || '').toLowerCase().includes(q) ||
        (c.cardholderName || c.fields?.cardholderName || '').toLowerCase().includes(q) ||
        (c.cardNumber || c.fields?.cardNumber || '').includes(q) ||
        (c.tags || []).some(t => t.toLowerCase().includes(q))
      );
      const matchTag = selectedTag === 'all' || (c.tags || []).includes(selectedTag);
      return matchQuery && matchTag;
    });
  }, [cardsList, q, selectedTag]);

  const filteredPasswords = useMemo(() => {
    return passwordsList.filter(p => {
      const matchQuery = !q || (
        (p.title || p.name || '').toLowerCase().includes(q) ||
        (p.username || p.email || '').toLowerCase().includes(q) ||
        (p.service || '').toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q))
      );
      const matchTag = selectedTag === 'all' || (p.tags || []).includes(selectedTag);
      return matchQuery && matchTag;
    });
  }, [passwordsList, q, selectedTag]);

  const filteredDocs = useMemo(() => {
    return docsList.filter(d => {
      const matchQuery = !q || (
        (d.title || d.fileName || '').toLowerCase().includes(q) ||
        (d.category || '').toLowerCase().includes(q) ||
        (d.format || '').toLowerCase().includes(q) ||
        (d.tags || []).some(t => t.toLowerCase().includes(q))
      );
      const matchTag = selectedTag === 'all' || (d.tags || []).includes(selectedTag);
      return matchQuery && matchTag;
    });
  }, [docsList, q, selectedTag]);

  const filteredExpenses = useMemo(() => {
    return expensesList.filter(e => {
      const matchQuery = !q || (
        (e.merchant || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        (e.tags || []).some(t => t.toLowerCase().includes(q))
      );
      const matchTag = selectedTag === 'all' || (e.tags || []).includes(selectedTag);
      return matchQuery && matchTag;
    });
  }, [expensesList, q, selectedTag]);

  const totalResultsCount = filteredCards.length + filteredPasswords.length + filteredDocs.length + filteredExpenses.length;

  if (!isOpen) return null;

  return (
    <div className="mobile-explore-overlay animate-fade-in" role="dialog" aria-modal="true">
      {/* Frosted iOS Header with Integrated Search */}
      <div className="mobile-explore-header">
        <div className="mobile-explore-top-row">
          <div className="mobile-explore-search-box">
            <Search size={17} className="mobile-explore-search-icon" />
            <input
              type="text"
              className="mobile-explore-search-input"
              placeholder="Search cards, passwords, docs, expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="mobile-explore-clear-btn"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="mobile-explore-cancel-btn"
            onClick={onClose}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>

        {/* Category Pills Slider */}
        <div className="mobile-explore-pills-row">
          {[
            { id: 'all', label: `All (${totalResultsCount})` },
            { id: 'cards', label: `Cards (${filteredCards.length})` },
            { id: 'passwords', label: `Passwords (${filteredPasswords.length})` },
            { id: 'documents', label: `Docs (${filteredDocs.length})` },
            { id: 'expenses', label: `Expenses (${filteredExpenses.length})` },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`mobile-explore-pill ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Trending Tags (Instagram Style) */}
        <div className="mobile-explore-tags-row">
          <span className="mobile-explore-tags-label">Trending:</span>
          {TRENDING_TAGS.map(tag => (
            <button
              key={tag}
              type="button"
              className={`mobile-explore-tag-chip ${selectedTag === tag ? 'active' : ''}`}
              onClick={() => setSelectedTag(tag)}
            >
              #{tag}
            </button>
          ))}
        </div>
      </div>

      {/* Explore Content Body - Mosaic & Interactive Cards */}
      <div className="mobile-explore-body">
        {totalResultsCount === 0 ? (
          <div className="mobile-explore-empty-state">
            <Search size={42} opacity={0.3} />
            {searchQuery ? (
              <>
                <h4>No items matched "{searchQuery}"</h4>
                <p>Try searching by issuer name, password service, tag, or payee.</p>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => { setSearchQuery(''); setSelectedTag('all'); setActiveTab('all'); }}
                >
                  Reset Filters
                </button>
              </>
            ) : (
              <>
                <h4>No vault items found</h4>
                <p>Logins, payment cards, and documents you save will appear here in real-time.</p>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => { onClose(); navigate('/vault'); }}
                >
                  Add Item to Vault
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* 1. VISUAL PAYMENT CARDS SECTION */}
            {(activeTab === 'all' || activeTab === 'cards') && filteredCards.length > 0 && (
              <section className="mobile-explore-section">
                <div className="mobile-explore-section-header">
                  <div className="section-title-wrap">
                    <CreditCard size={18} className="text-warning" />
                    <h3>Payment Cards</h3>
                    <span className="section-counter">{filteredCards.length}</span>
                  </div>
                  <button
                    type="button"
                    className="section-see-all-link"
                    onClick={() => { onClose(); navigate('/apps/cards'); }}
                  >
                    View App <ChevronRight size={14} />
                  </button>
                </div>

                <div className="mobile-cards-mosaic-grid">
                  {filteredCards.map(c => {
                    const f = c.fields || c;
                    const isRevealed = Boolean(revealedIds[c.id]);
                    const cardNum = f.cardNumber || c.cardNumber || '•••• •••• •••• 4242';
                    const rawNum = f.rawNumber || c.rawNumber || '4242881900214242';

                    return (
                      <div
                        key={c.id}
                        className="mobile-explore-card-item"
                        style={{ background: c.gradient || 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}
                      >
                        <div className="card-item-top">
                          <div className="card-chip" style={{ background: c.chipColor || '#f59e0b' }} />
                          <span className="card-network-badge">{c.network || 'Visa'}</span>
                        </div>

                        <div className="card-item-title-group">
                          <div className="card-title-text">{c.cardName || c.title || 'Virtual Card'}</div>
                          <div className="card-issuer-sub">{f.issuer || c.issuer || 'Payment Card'}</div>
                        </div>

                        <div className="card-number-display">
                          {isRevealed ? rawNum : cardNum}
                        </div>

                        <div className="card-item-footer">
                          <div className="card-holder-col">
                            <span className="card-meta-label">CARDHOLDER</span>
                            <span className="card-meta-val">{f.cardholderName || c.cardholderName || user?.name || 'Authorized User'}</span>
                          </div>
                          <div className="card-expiry-col">
                            <span className="card-meta-label">EXPIRES</span>
                            <span className="card-meta-val">{f.expiry || c.expiry || '08/29'}</span>
                          </div>
                        </div>

                        {/* Quick 1-Tap Action Floating Controls */}
                        <div className="mobile-card-quick-actions">
                          <button
                            type="button"
                            className="card-quick-action-btn"
                            onClick={() => handleCopy(rawNum.replace(/\s+/g, ''), `card-${c.id}`)}
                            title="Copy Card Number"
                          >
                            {copiedId === `card-${c.id}` ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
                            <span>{copiedId === `card-${c.id}` ? 'Copied' : 'Copy'}</span>
                          </button>
                          <button
                            type="button"
                            className="card-quick-action-btn"
                            onClick={() => toggleReveal(c.id)}
                            title="Reveal Card Details"
                          >
                            {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
                            <span>{isRevealed ? 'Hide' : 'Reveal'}</span>
                          </button>
                          <button
                            type="button"
                            className="card-quick-action-btn"
                            onClick={() => { onClose(); navigate('/apps/cards'); }}
                            title="Open Card"
                          >
                            <ExternalLink size={13} />
                            <span>Open</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 2. PASSWORDS & VAULT CREDENTIALS SECTION */}
            {(activeTab === 'all' || activeTab === 'passwords') && filteredPasswords.length > 0 && (
              <section className="mobile-explore-section">
                <div className="mobile-explore-section-header">
                  <div className="section-title-wrap">
                    <KeyRound size={18} className="text-primary" />
                    <h3>Passwords & Logins</h3>
                    <span className="section-counter">{filteredPasswords.length}</span>
                  </div>
                  <button
                    type="button"
                    className="section-see-all-link"
                    onClick={() => { onClose(); navigate('/vault'); }}
                  >
                    View All <ChevronRight size={14} />
                  </button>
                </div>

                <div className="mobile-passwords-explore-grid">
                  {filteredPasswords.map(p => (
                    <div key={p.id} className="mobile-pwd-card glass-panel">
                      <div className="pwd-card-left">
                        <div
                          className="pwd-avatar-box"
                          style={{ background: `${p.color || '#3b82f6'}20`, color: p.color || '#3b82f6' }}
                        >
                          <KeyRound size={18} />
                        </div>
                        <div className="pwd-info">
                          <div className="pwd-title">{p.title || p.name}</div>
                          <div className="pwd-username">{p.username || p.email || 'account user'}</div>
                          <div className="pwd-strength-row">
                            <span className="strength-bar-pill">
                              <span className="strength-fill" style={{ width: `${p.strength || 88}%` }} />
                            </span>
                            <span className="strength-text">{p.strengthLabel || 'Strong (92%)'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pwd-card-actions">
                        <button
                          type="button"
                          className="btn-icon btn-sm"
                          onClick={() => handleCopy('Cipher$ecur3!2026', `pwd-${p.id}`)}
                          title="Copy Password"
                        >
                          {copiedId === `pwd-${p.id}` ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-sm"
                          onClick={() => { onClose(); navigate('/vault'); }}
                          title="Open Entry"
                        >
                          <ExternalLink size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 3. ENCRYPTED DOCUMENTS SECTION */}
            {(activeTab === 'all' || activeTab === 'documents') && filteredDocs.length > 0 && (
              <section className="mobile-explore-section">
                <div className="mobile-explore-section-header">
                  <div className="section-title-wrap">
                    <FolderLock size={18} className="text-info" />
                    <h3>Zero-Knowledge Documents</h3>
                    <span className="section-counter">{filteredDocs.length}</span>
                  </div>
                  <button
                    type="button"
                    className="section-see-all-link"
                    onClick={() => { onClose(); navigate('/apps/documents'); }}
                  >
                    View App <ChevronRight size={14} />
                  </button>
                </div>

                <div className="mobile-docs-explore-grid">
                  {filteredDocs.map(d => (
                    <div
                      key={d.id}
                      className="mobile-doc-card glass-panel"
                      onClick={() => { onClose(); navigate('/apps/documents'); }}
                    >
                      <div className="doc-card-top-row">
                        <span className={`doc-format-badge ${d.badgeClass || 'format-pdf'}`}>
                          {d.format || 'PDF'}
                        </span>
                        <span className="doc-category-tag">{d.category || 'Personal'}</span>
                      </div>
                      <h4 className="doc-card-title">{d.title}</h4>
                      <div className="doc-card-sub">{d.fileName || d.fileSize || 'Encrypted File'}</div>
                      <div className="doc-card-bottom-row">
                        <span className="doc-expiry-pill">{d.expiry || 'Protected'}</span>
                        <div className="doc-quick-btn-group" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="doc-mini-btn"
                            onClick={() => { onClose(); navigate('/apps/documents'); }}
                          >
                            <Download size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 4. EXPENSE TRANSACTIONS SECTION */}
            {(activeTab === 'all' || activeTab === 'expenses') && filteredExpenses.length > 0 && (
              <section className="mobile-explore-section">
                <div className="mobile-explore-section-header">
                  <div className="section-title-wrap">
                    <Wallet size={18} className="text-success" />
                    <h3>Recent Transactions</h3>
                    <span className="section-counter">{filteredExpenses.length}</span>
                  </div>
                  <button
                    type="button"
                    className="section-see-all-link"
                    onClick={() => { onClose(); navigate('/apps/expense-tracker'); }}
                  >
                    Tracker <ChevronRight size={14} />
                  </button>
                </div>

                <div className="mobile-expenses-explore-list">
                  {filteredExpenses.map(e => {
                    const fullDetails = `${e.merchant || e.description || 'Transaction'} - ${e.category || 'General'} (${e.currency || '₹'}${Number(e.amount).toLocaleString()})`;
                    return (
                      <div
                        key={e.id}
                        className="mobile-trn-row glass-panel"
                        onClick={() => { onClose(); navigate('/apps/expense-tracker'); }}
                        title={fullDetails}
                      >
                        <div className="trn-left">
                          <div className="trn-icon-circle">
                            <TrendingUp size={16} />
                          </div>
                          <div className="trn-text-col">
                            <div
                              className="trn-merchant"
                              title={e.merchant || e.description || 'Expense'}
                            >
                              {e.merchant || e.description || 'Expense'}
                            </div>
                            <div
                              className="trn-sub"
                              title={`${e.category || 'General'} • ${e.date || 'Recent'}`}
                            >
                              {e.category || 'General'} • {e.date || 'Recent'}
                            </div>
                          </div>
                        </div>
                        <div className="trn-right">
                          <div className="trn-amount">
                            {e.currency || '₹'}{Number(e.amount).toLocaleString()}
                          </div>
                          <div className="trn-method">{e.paymentMethod || 'Cash/Card'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
